import React, { useState } from 'react';
import * as M from '@material-ui/core';
import { api } from '@services/api';
import { LoadingPage } from '@components/LoadingPage';
import { cpfValidation, maskCPF, maskCNPJ } from '@utils/masks';
import { ToastMessage } from '@components/ToastMessage';
import { type Color } from '@material-ui/lab';
import { Kit as KitData } from '@dtos/kitDTO';
import { PreCadastroKit } from './PreCadastroKit';
import { AppError } from '@utils/AppError';

// Estados possíveis após a consulta
type KitStatus =
  | 'idle'           // tela inicial
  | 'preencher'      // primeiro preenchimento ou nova empresa confirmada
  | 'ja_preenchida'  // ficha já existe — oferece opção de nova empresa
  | 'nova_empresa'   // colaborador quer preencher para nova empresa — aguarda CNPJ
  | 'salvo';         // ficha salva com sucesso

interface EmpresaInfo {
  cnpj: string;
  razao_social: string;
  genero: string;
  nova_empresa: boolean;
}

export default function Kit() {
  const [antecendente, setAntecedente] = useState<KitData>({} as KitData);
  const [cpfColab, setCpfColab] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [cnpjNovaEmpresa, setCnpjNovaEmpresa] = useState('');
  const [empresa, setEmpresa] = useState<EmpresaInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [kitStatus, setKitStatus] = useState<KitStatus>('idle');
  const [validationCpf, setValidationCpf] = useState<boolean>(true);
  const [name, setName] = useState('');
  const [dateBirth, setDateBirth] = useState('');
  const [messageServer, setMessageServer] = useState({
    code: 0,
    message: '',
    severity: '' as Color,
    show: false
  });

  function showToast(message: string, severity: Color, code: number) {
    setMessageServer({ code, message, severity, show: true });
  }

  function validarCPF(cpf: string) {
    cpf = cpf.replace(/[.-]/g, '');
    setValidationCpf(!cpfValidation(cpf));
    return cpf;
  }

  async function handleConsultar() {
    if (!cpfColab || !dataNascimento) {
      showToast('Preencha o CPF e a Data de Nascimento para continuar.', 'warning', 400);
      return;
    }

    try {
      setLoading(true);
      setKitStatus('idle');
      setEmpresa(null);

      const res = await api.post('/ravim/colab-kit', {
        cpf_colaborador: cpfColab,
        data_nascimento: dataNascimento
      });

      const data = res.data;
      setEmpresa({
        cnpj: data.cnpj,
        razao_social: data.razao_social,
        genero: data.genero,
        nova_empresa: false
      });
      setKitStatus('preencher');
      showToast('Colaborador identificado. Preencha a ficha abaixo.', 'success', 200);
    } catch (error: any) {
      // O interceptor do Axios transforma erros em AppError({ message, code })
      // então lemos error.code em vez de error.response.status
      const status = error?.code ?? error?.response?.status;
      const cnpjErr = error?.response?.data?.cnpj || '';
      const razaoErr = error?.response?.data?.razao_social || '';
      const generoErr = error?.response?.data?.genero || '';

      if (status === 409) {
        // Ficha já preenchida — oferece opção de nova empresa
        setEmpresa({
          cnpj: cnpjErr,
          razao_social: razaoErr,
          genero: generoErr,
          nova_empresa: false
        });
        setKitStatus('ja_preenchida');
        showToast('Ficha clínica já preenchida.', 'info', 409);
      } else if (status === 404) {
        setKitStatus('idle');
        showToast(error?.message || 'Dados não conferem. Verifique CPF e Data de Nascimento.', 'error', 404);
      } else {
        setKitStatus('idle');
        showToast(error?.message || 'Erro ao consultar dados. Tente novamente.', 'error', status || 500);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmarNovaEmpresa() {
    if (!cnpjNovaEmpresa.trim()) {
      showToast('Informe o CNPJ da nova empresa.', 'warning', 400);
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/ravim/colab-kit-nova-empresa', {
        cpf_colaborador: cpfColab,
        cnpj_empresa: cnpjNovaEmpresa.replace(/\D/g, '')
      });

      const data = res.data;
      setEmpresa({
        cnpj: data.cnpj,
        razao_social: data.razao_social,
        genero: data.genero,
        nova_empresa: true
      });
      setKitStatus('preencher');
      showToast('Nova empresa confirmada. Preencha a ficha abaixo.', 'success', 200);
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      showToast(data?.message || 'CNPJ não encontrado na base.', 'error', status || 404);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSendKit() {
    try {
      const data = {
        cpf_colaborador: cpfColab,
        nome_colaborador: antecendente.nome_colaborador || name,
        nascimento: dateBirth,
        cnpj_empresa: empresa?.cnpj || '',
        peso: antecendente.peso || '',
        altura: antecendente.altura || '',
        Diabetes_1: antecendente.Diabetes_1 || '',
        Parentesco_1: antecendente.Parentesco_1 || '',
        Parentesco_2: antecendente.Parentesco_2 || '',
        Parentesco_3: antecendente.Parentesco_3 || '',
        Parentesco_4: antecendente.Parentesco_4 || '',
        Parentesco_5: antecendente.Parentesco_5 || '',
        Parentesco_6: antecendente.Parentesco_6 || '',
        Parentesco_7: antecendente.Parentesco_7 || '',
        Parentesco_8: antecendente.Parentesco_8 || '',
        Doenca_do_Coracao: antecendente.Doenca_do_Coracao || '',
        Doenca_Psiquiatricas: antecendente.Doenca_Psiquiatricas || '',
        Tuberculose: antecendente.Tuberculose || '',
        Cancer: antecendente.Cancer || '',
        Pressao_Alta: antecendente.Pressao_Alta || '',
        Alergias: antecendente.Alergias || '',
        Outras: antecendente.Outras || '',
        Doencas_do_coracao_Pressoao_Alta: antecendente.Doencas_do_coracao_Pressoao_Alta || '',
        Bronquite_Asma_Rinite: antecendente.Bronquite_Asma_Rinite || '',
        Hemorroidas: antecendente.Hemorroidas || '',
        Doencas_renais: antecendente.Doencas_renais || '',
        Hernias: antecendente.Hernias || '',
        Doencas_do_figado: antecendente.Doencas_do_figado || '',
        Alguma_deficiencia_fisica: antecendente.Alguma_deficiencia_fisica || '',
        Diabetes_2: antecendente.Diabetes_2 || '',
        Ja_foi_submetido_a_cirurgia: antecendente.Ja_foi_submetido_a_cirurgia || '',
        Doencas_do_estomago: antecendente.Doencas_do_estomago || '',
        Ja_esteve_internado: antecendente.Ja_esteve_internado || '',
        Resfriados_frequentes_Sinusite: antecendente.Resfriados_frequentes_Sinusite || '',
        Dificuldade_em_tarefa_pesada: antecendente.Dificuldade_em_tarefa_pesada || '',
        Problemas_de_visao: antecendente.Problemas_de_visao || '',
        Tabagismo: antecendente.Tabagismo || '',
        Problemas_de_audicao: antecendente.Problemas_de_audicao || '',
        Etilismo_bebe: antecendente.Etilismo_bebe || '',
        Otite_cronica_Zumbido_no_ouvido: antecendente.Otite_cronica_Zumbido_no_ouvido || '',
        Se_considera_acima_do_peso: antecendente.Se_considera_acima_do_peso || '',
        Dor_de_cabeca_Tontura_Convulsoes: antecendente.Dor_de_cabeca_Tontura_Convulsoes || '',
        Sofreu_ou_sofre_de_insonia: antecendente.Sofreu_ou_sofre_de_insonia || '',
        Doencas_de_pele: antecendente.Doencas_de_pele || '',
        Se_considera_estressado: antecendente.Se_considera_estressado || '',
        Dores_nas_costas: antecendente.Dores_nas_costas || '',
        Pratica_esporte_regularmente: antecendente.Pratica_esporte_regularmente || '',
        Reumatismo_Dor_nas_juntas: antecendente.Reumatismo_Dor_nas_juntas || '',
        Faz_uso_de_medicamento: antecendente.Faz_uso_de_medicamento || '',
        Sofreu_alguma_fratura_ossea: antecendente.Sofreu_alguma_fratura_ossea || '',
        Outra_doenca: antecendente.Outra_doenca || '',
        Doenca_psiquiatrica: antecendente.Doenca_psiquiatrica || '',
        Afastado_por_mais_de_15_dias: antecendente.Afastado_por_mais_de_15_dias || '',
        Ja_sofreu_acidente_trabalho: antecendente.Ja_sofreu_acidente_trabalho || '',
        Recebeu_indenizacao_acidente_trabalho: antecendente.Recebeu_indenizacao_acidente_trabalho || '',
        Ja_apresentou_alguma_doenca_trabalho_profssional: antecendente.Ja_apresentou_alguma_doenca_trabalho_profssional || '',
        Ja_se_submeteu_exame_complementar_anterior: antecendente.Ja_se_submeteu_exame_complementar_anterior || '',
        Ja_apresentou_afastamento_superior_a_15_dias: antecendente.Ja_apresentou_afastamento_superior_a_15_dias || '',
        Suas_condicoes_de_saude_exigem: antecendente.Suas_condicoes_de_saude_exigem || '',
        Ja_exerceu_atividade_com_uso_de_EPI: antecendente.Ja_exerceu_atividade_com_uso_de_EPI || '',
        Necessita_de_uso_de_EPI_em_suas: antecendente.Necessita_de_uso_de_EPI_em_suas || '',
        Data_da_ultima_mestruacao_caso_Sexo_Feminino: antecendente.Data_da_ultima_mestruacao_caso_Sexo_Feminino || '',
        observacoes: antecendente.observacoes || '',
        Varizes: antecendente.Varizes || '',
        Nome_doenca: antecendente.Nome_doenca || '',
        Nome_doenca_2: antecendente.Nome_doenca_2 || ''
      };

      await api.post('/ravim/kit', data);
      showToast('Ficha clínica salva com sucesso!', 'success', 200);
      setKitStatus('salvo');
    } catch (error) {
      if (error instanceof AppError) {
        showToast(error.message, 'error', error.code);
      }
    }
  }

  function handleSelectedValueRadio(name: string, value: string) {
    setAntecedente((prevState) => ({
      ...prevState,
      [name]: value
    }));
  }

  return (
    <M.Container>
      <LoadingPage open={loading} onClick={() => null} />

      {/* Painel de consulta: CPF + Data de Nascimento */}
      <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
        <M.Paper style={{ padding: 24, marginTop: 24, width: '65%' }}>
          <M.Typography variant="h6" style={{ textAlign: 'center', marginBottom: 16, fontWeight: 'bold' }}>
            Ficha Clínica
          </M.Typography>
          <M.FormGroup style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <M.TextField
              placeholder="CPF"
              label="CPF"
              variant="outlined"
              style={{ flex: 1, minWidth: 160 }}
              value={cpfColab}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConsultar(); }}
              onChange={(e) => {
                setCpfColab(maskCPF(validarCPF(e.target.value)));
              }}
              inputProps={{ maxLength: 14 }}
            />
            <M.TextField
              label="Data de Nascimento"
              variant="outlined"
              type="date"
              style={{ flex: 1, minWidth: 180 }}
              value={dataNascimento}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConsultar(); }}
              onChange={(e) => setDataNascimento(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <M.Button
              variant="contained"
              color={validationCpf || !dataNascimento ? 'default' : 'secondary'}
              onClick={handleConsultar}
              disabled={validationCpf || !dataNascimento}
              style={{ height: 56, minWidth: 140 }}
            >
              Consultar Kit
            </M.Button>
          </M.FormGroup>
        </M.Paper>
      </div>

      {/* Ficha já preenchida — com opção de nova empresa */}
      {kitStatus === 'ja_preenchida' && (
        <M.Paper style={{ padding: 24, marginTop: 24, backgroundColor: '#f0f4ff' }}>
          <M.Typography variant="h6" style={{ color: '#1a237e', fontWeight: 'bold', marginBottom: 8 }}>
            ✅ Ficha clínica já preenchida
          </M.Typography>
          <M.Typography variant="body1" style={{ color: '#555', marginBottom: 16 }}>
            Sua ficha clínica já foi registrada. Caso tenha mudado de empresa, informe o CNPJ da nova empresa para preencher novamente.
          </M.Typography>
          <M.Button
            variant="outlined"
            color="secondary"
            onClick={() => setKitStatus('nova_empresa')}
          >
            Mudei de empresa
          </M.Button>
        </M.Paper>
      )}

      {/* Fluxo de nova empresa — campo CNPJ */}
      {kitStatus === 'nova_empresa' && (
        <M.Paper style={{ padding: 24, marginTop: 24 }}>
          <M.Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 12 }}>
            Informe o CNPJ da nova empresa
          </M.Typography>
          <M.Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
            O CNPJ será verificado na base de dados. Se encontrado, você poderá preencher a ficha novamente.
          </M.Typography>
          <M.FormGroup style={{ flexDirection: 'row', gap: 16, alignItems: 'center' }}>
            <M.TextField
              label="CNPJ da nova empresa"
              variant="outlined"
              style={{ flex: 1, maxWidth: 300 }}
              value={cnpjNovaEmpresa}
              onChange={(e) => setCnpjNovaEmpresa(e.target.value)}
              inputProps={{ maxLength: 18 }}
              placeholder="00.000.000/0000-00"
            />
            <M.Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmarNovaEmpresa}
              disabled={!cnpjNovaEmpresa.trim()}
              style={{ height: 56 }}
            >
              Confirmar
            </M.Button>
            <M.Button
              variant="text"
              onClick={() => setKitStatus('ja_preenchida')}
              style={{ height: 56 }}
            >
              Cancelar
            </M.Button>
          </M.FormGroup>
        </M.Paper>
      )}

      {/* Ficha salva com sucesso */}
      {kitStatus === 'salvo' && (
        <M.Paper style={{ padding: 24, marginTop: 24, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
          <M.Typography variant="h6" style={{ color: '#1b5e20', fontWeight: 'bold' }}>
            ✅ Ficha clínica salva com sucesso!
          </M.Typography>
          <M.Typography variant="body1" style={{ marginTop: 8, color: '#555' }}>
            Seus dados foram registrados. Obrigado!
          </M.Typography>
        </M.Paper>
      )}

      {/* Empresa identificada + formulário de preenchimento */}
      {kitStatus === 'preencher' && empresa && (
        <>
          <M.Paper style={{ padding: 20, marginTop: 20 }}>
            <M.Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
              Empresa identificada
            </M.Typography>
            {empresa.nova_empresa && (
              <M.Typography variant="body2" style={{ color: '#e65100', marginBottom: 8 }}>
                ⚠️ Nova empresa confirmada. Por favor, preencha a ficha novamente.
              </M.Typography>
            )}
            <M.Typography variant="body1">
              <strong>CNPJ:</strong> {empresa.cnpj ? maskCNPJ(empresa.cnpj) : empresa.cnpj}
            </M.Typography>
          </M.Paper>

          <PreCadastroKit
            genero={empresa.genero}
            user={[]}
            fetchSendKit={fetchSendKit}
            handleSelectedValueRadio={handleSelectedValueRadio}
            cpf={cpfColab}
            handleCpf={() => {}}
            handleNomeCompleto={(value) => setName(value)}
            handleNomeDataDeNascimento={(value) => setDateBirth(value)}
            value={antecendente}
          />
        </>
      )}

      <ToastMessage
        code={String(messageServer.code)}
        message={messageServer.message}
        show={messageServer.show}
        severity={messageServer.severity}
        onClose={() => {
          setMessageServer({ ...messageServer, show: false });
        }}
      />
    </M.Container>
  );
}
